import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { getFormErrorMessage } from '../../../../config/global';
import { setSelectedGroup } from '../../../../redux/Slices/deviceSlice';
import { ProgressSpinner } from 'primereact/progressspinner';
import { toggleDialog } from '../../../../redux/Slices/Global/globalSlice';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { SplitButtonCustom } from '../../../SharedComponents/SplitBtn/SplitButtonCustom';

import {
    useDeleteGroupMutation,
    useFlushGroupMutation,
    useMuteUnmuteGroupMutation,
    usePlayPauseGroupMutation,
    useRestartGroupMutation,
    useUpdateGroupMutation
} from '../../../../queries/group';

// Exact icon paths from .claude/pp-screens-prototype.html's .ghead
// (grip/dots). Self-contained sizing/stroke so they render correctly
// regardless of what wraps them (incl. PrimeReact's icon-wrapping internals
// for the SplitButton's dropdownIcon).
const ICON_PROPS = { width: 17, height: 17, viewBox: '0 0 24 24', stroke: 'currentColor', fill: 'none', strokeWidth: 1.9 };
const IcDots = () => <svg {...ICON_PROPS}><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>;
const IcCheck = () => <svg {...ICON_PROPS}><path d="M5 12l4 4L19 7" /></svg>;
// .grip — fill, not stroke, per the prototype's exact rule.
const IcGrip = () => (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="currentColor">
        <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
        <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
        <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
    </svg>
);

// ⋮ menu icons — exact paths from .claude/pp-dialogs-prototype.html's group
// menu, plus play/pause/order/move icons in the same line-icon style for the
// items the prototype's static example doesn't happen to depict.
const IcPlus = () => <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>;
const IcCalendar = () => <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>;
const IcRename = () => <svg viewBox="0 0 24 24"><path d="M4 6h12M4 12h16M4 18h9" /></svg>;
const IcSpeaker = () => <svg viewBox="0 0 24 24"><path d="M11 5 6 9H3v6h3l5 4zM16 9a4 4 0 0 1 0 6" /></svg>;
const IcRestart = () => <svg viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-3-6.7M21 4v5h-5" /></svg>;
const IcBinOutline = () => <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>;
const IcBinFull = () => <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>;
const IcPlay = () => <svg viewBox="0 0 24 24"><path d="M7 5l12 7-12 7z" /></svg>;
const IcPause = () => <svg viewBox="0 0 24 24"><rect x="7" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>;
const IcArrowUp = () => <svg viewBox="0 0 24 24"><path d="M12 19V5M6 11l6-6 6 6" /></svg>;
const IcArrowDown = () => <svg viewBox="0 0 24 24"><path d="M12 5v14M6 13l6 6 6-6" /></svg>;
const IcClose = () => <svg width="18" height="18" viewBox="0 0 24 24" style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 2 }}><path d="M6 6l12 12M18 6L6 18" /></svg>;

function GroupHeader({ groupInfo, assignSchedule, onMoveGroup, isFirst, isLast, dragHandleProps }) {
    const { t } = useTranslation();
    const { formState: { errors }, control, handleSubmit, reset } = useForm();
    const dispatch = useDispatch();
    const fetchingCampaign = useSelector((state) => state.global.isLoading);

    const restartGroupDevices = useRestartGroupMutation();
    const muteUnmuteGroup = useMuteUnmuteGroupMutation()
    const playPauseGroup = usePlayPauseGroupMutation()
    const updateGroup = useUpdateGroupMutation();
    const deleteGroup = useDeleteGroupMutation();
    const flushGroup = useFlushGroupMutation();

    const isDefaultGroup = useMemo(() => groupInfo?.name.toLowerCase() === "default", [groupInfo?.name])
    const hasDevices = groupInfo?.items?.length > 0;
    const groupID = groupInfo.group_id;

    const [editName, setEditName] = useState(false);
    const [name, setName] = useState(groupInfo?.name);
    // Gates delete/restart-all/flush-all behind a confirm dialog — these used
    // to fire straight off the menu click with no confirmation at all (a
    // stray tap could wipe a whole group). null = dialog closed.
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        reset({ "name": name })
    }, [editName])

    const onSubmit = async (data) => {
        setEditName(false)
        setName(data.name)
        if (groupInfo?.name != data.name) {
            await updateGroup.mutateAsync({
                id: groupID,
                data: data
            })
        }
    }

    const deleteGroupHandler = async () => {
        await deleteGroup.mutateAsync({ id: groupID })
        setConfirmAction(null)
    }

    const flushHandler = async () => {
        await flushGroup.mutateAsync({ id: groupID })
        setConfirmAction(null)
    }

    const restartHandler = async () => {
        await restartGroupDevices.mutateAsync({ id: groupID })
        setConfirmAction(null)
    }

    const muteHandler = async (action) => {
        await muteUnmuteGroup.mutateAsync({
            id: groupID,
            data: { "action": action, "id": groupID, }
        })
    }

    const playPauseHandler = async (action) => {
        await playPauseGroup.mutateAsync({
            group_id: groupID,
            action: action
        })
    }

    const assignCampaignHandler = async () => {
        dispatch(setSelectedGroup(groupInfo))
        dispatch(toggleDialog("GroupAssignCampaignsDropDown"));
    }


    // Sectioned per .claude/pp-dialogs-prototype.html's group ⋮ menu
    // (المجموعة / للجميع + a separated red حذف). Delete, restart-all and
    // flush-all now open a confirm dialog instead of firing immediately —
    // per the prototype's own note, these previously executed with zero
    // confirmation. Order/move-up/move-down are real existing features the
    // prototype's static example just doesn't happen to depict; kept, in
    // their own trailing section rather than dropped.
    const items = [
        { type: 'label', text: t('devices.menu_section_group') },
        {
            label: <span className="fs-6">{t('devices.group_assign_campaign')}</span>,
            icon: <IcPlus />,
            command: fetchingCampaign ? "" : assignCampaignHandler,
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('devices.group_assign_schedule')}</span>,
            icon: <IcCalendar />,
            command: () => assignSchedule(groupInfo),
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('devices.group_edit_group_name')}</span>,
            icon: <IcRename />,
            command: () => setEditName(true),
            visible: !isDefaultGroup
        },

        { type: 'sep' },
        { type: 'label', text: t('devices.menu_section_all') },
        {
            label: <span className="fs-6">{t('devices.group_play')}</span>,
            icon: <IcPlay />,
            command: () => playPauseHandler("play"),
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('buttons.pause')}</span>,
            icon: <IcPause />,
            command: () => playPauseHandler("pause"),
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('devices.group_mute')}</span>,
            icon: <IcSpeaker />,
            command: () => muteHandler("mute"),
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('devices.group_unmute')}</span>,
            icon: <IcSpeaker />,
            command: () => muteHandler("unmute"),
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('devices.group_restart')}</span>,
            icon: <IcRestart />,
            command: () => setConfirmAction('restart'),
            disabled: !hasDevices
        },
        {
            label: <span className="fs-6">{t('devices.group_flush')}</span>,
            icon: <IcBinOutline />,
            command: () => setConfirmAction('flush'),
            disabled: !hasDevices
        },

        { type: 'sep' },
        { type: 'label', text: t('devices.menu_section_order') },
        {
            label: <span className="fs-6">{t('devices.group_move_up')}</span>,
            icon: <IcArrowUp />,
            command: () => onMoveGroup(groupID, 'up'),
            disabled: !!isFirst
        },
        {
            label: <span className="fs-6">{t('devices.group_move_down')}</span>,
            icon: <IcArrowDown />,
            command: () => onMoveGroup(groupID, 'down'),
            disabled: !!isLast
        },

        { type: 'sep' },
        {
            label: <span className="fs-6">{t('devices.group_delete_group')}</span>,
            icon: <IcBinFull />,
            command: () => setConfirmAction('delete'),
            visible: !isDefaultGroup,
            danger: true
        }
    ]

    const isSpinning = restartGroupDevices.isLoading || muteUnmuteGroup.isLoading || updateGroup.isLoading || deleteGroup.isLoading || flushGroup?.isLoading || playPauseGroup.isLoading;

    return (
        <div className="pp-scr-ghead">
            <span className="grip" title={t('devices.group_drag_reorder')} {...dragHandleProps}>
                <IcGrip />
            </span>
            {editName ? (
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-grow-1">
                    <div className={`field flex-grow-1 ${errors.name ? "mx-2 mt-2" : "m-2"}`}>
                        <span className="p-float-label">
                            <Controller name="name" control={control}
                                rules={{ required: t('devices.group_name_required_excl') }}
                                render={({ field, fieldState }) => {
                                    return (
                                        <InputText
                                            id={field.name} {...field} autoFocus
                                            className={`w-100 p-1 ${classNames({ 'p-invalid': fieldState.invalid })}`} />
                                    )
                                }} />
                        </span>
                    </div>
                    <button className="pp-scr-iconbtn" type='submit' title={t('buttons.update')} aria-label={t('buttons.update')}>
                        <IcCheck />
                    </button>
                </form>
            ) : (
                <>
                    <span className="gname">{name}</span>
                    <span className="gcount tnum">{t('devices.screen_count', { count: groupInfo?.items?.length ?? 0 })}</span>
                </>
            )}

            <div className="gacts">
                {!editName && (
                    <>
                        {isSpinning && <ProgressSpinner className='w-[20px] h-[20px]' strokeWidth="4" />}

                        <SplitButtonCustom items={items} className="pp-scr-group-more" icon={<IcDots />} />
                    </>
                )}
            </div>

            <div className='mx-2'>{getFormErrorMessage('name', errors)}</div>

            {confirmAction && (() => {
                const cfg = {
                    delete: { icon: <IcBinFull />, crit: true, title: t('devices.group_delete_group'), msg: t('devices.group_delete_confirm_msg', { name }), run: deleteGroupHandler, loading: deleteGroup.isLoading },
                    restart: { icon: <IcRestart />, crit: false, title: t('devices.group_restart'), msg: t('devices.group_restart_confirm_msg', { name }), run: restartHandler, loading: restartGroupDevices.isLoading },
                    flush: { icon: <IcBinOutline />, crit: false, title: t('devices.group_flush'), msg: t('devices.group_flush_confirm_msg', { name }), run: flushHandler, loading: flushGroup.isLoading },
                }[confirmAction];
                return (
                    <Dialog visible style={{ width: '420px' }} showHeader={false} modal className="pp-dlg" onHide={() => setConfirmAction(null)}>
                        <div className="pp-dlg-hd">
                            <span className={`ic ${cfg.crit ? 'crit' : ''}`}>{cfg.icon}</span>
                            <div>
                                <div className="t">{cfg.title}</div>
                                {cfg.crit && <div className="s">{t('devices.group_no_undo')}</div>}
                            </div>
                            <span className="x" onClick={() => setConfirmAction(null)}><IcClose /></span>
                        </div>
                        <div className="pp-dlg-bd"><p className="dlgtext">{cfg.msg}</p></div>
                        <div className="pp-dlg-ft">
                            <button className={`btn ${cfg.crit ? 'crit' : 'primary'}`} disabled={cfg.loading} onClick={cfg.run}>
                                {cfg.crit ? t('buttons.delete') : t('buttons.confirm')}
                            </button>
                            <button className="btn ghost" disabled={cfg.loading} onClick={() => setConfirmAction(null)}>{t('buttons.cancel')}</button>
                        </div>
                    </Dialog>
                );
            })()}
        </div>
    )
}

export default GroupHeader